import '../../styles/CaseViewer.css';

import { Fragment, useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, TooltipProps } from 'recharts';
import {
  ValueType,
  NameType,
} from 'recharts/types/component/DefaultTooltipContent';

import {
  CannulationTime,
  CaseReport,
  PathLength
} from '../../models/CaseTypes';
import CaseReportAdapter from '../../models/CaseReportAdapter';

const BREAKPOINT_MOBILE = 390;

interface EfficiencyData {
  cannulationTimes: CannulationTime[];
  pathLengths: PathLength[];
}

interface VertebraTime {
  vertebra: string;
  time: number;
}

interface VertebraPathLength {
  vertebra: string;
  total: number;
}

const adapter = new CaseReportAdapter();

/**
 * Component to display the Cannulation Technique
 * Shows cannulation times and path lengths for different vertebra
 */
const CannulationTechnique = ({ caseReport }: {caseReport: CaseReport }):
  React.JSX.Element => {
  const [efficiencyData, setEfficiencyData] = useState<EfficiencyData>({
    cannulationTimes: [],
    pathLengths: []
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!caseReport) {
      return;
    }

    const fetchEfficiencyData = async (): Promise<void> => {
      try {
        setLoading(true);

        // Set the efficiency data with sorted arrays
        setEfficiencyData({
          cannulationTimes: caseReport.cannulationTimes,
          pathLengths: caseReport.pathLengths
        });
      } catch (err: unknown) {
        console.error('Error fetching efficiency data:', err);
        setError('Could not load case efficiency data: ' + (err as Error).message);

        // Set empty data structure
        setEfficiencyData({
          cannulationTimes: [],
          pathLengths: []
        });
      } finally {
        setLoading(false);
      }
    };

    fetchEfficiencyData();
  }, [caseReport]);

  // Define custom tooltip component for better formatting
  const CustomTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>):
    React.JSX.Element  => {
    if (active && payload && payload.length) {
      // Calculate total if there are multiple values (for stacked bars)
      const total = payload.length > 1
        ? payload.reduce((sum, entry) => sum + Number(entry.value || '0'), 0)
        : payload[0].value;

      return (
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '10px',
          border: '1px solid #ddd',
          borderRadius: '4px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
        }}>
          <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>{`Vertebra: ${label}`}</p>
          {payload.map((entry, index) => (
            <p key={`item-${index}`} style={{
              margin: '2px 0',
              color: entry.color
            }}>
              {`${entry.name}: ${Number(entry.value).toFixed(1)}`}
            </p>
          ))}
          {payload.length > 1 && (
            <p style={{
              margin: '5px 0 0 0',
              fontWeight: 'bold',
              borderTop: '1px solid #eee',
              paddingTop: '5px'
            }}>
              {`Total: ${Number(total).toFixed(1)}`}
            </p>
          )}
        </div>
      );
    }
    return <Fragment/>;
  };

  // Render the cannulation times chart
  const renderCannulationTimesChart = (): React.JSX.Element => {
    if (efficiencyData.cannulationTimes.length === 0) {
      return (
        <div className="warning-message">
          <p>No cannulation time data available for this case.</p>
        </div>
      );
    }
    // Format data for the chart
    const chartData: VertebraTime[] = efficiencyData.cannulationTimes.map(item => ({
      vertebra: item.vertebra,
      time: item.time_minutes
    }));

    const pathWidth: string = `${Math.max(100, chartData.length * 12.5)}%`;

    // Then modify your chart container in renderCannulationTimesChart():
    return (
      <div>
        <div className="total-time-container">
          <span className="total-time-value">{getAvgCannulationTimeText()}</span>
          <div className="total-time-label">Avg. Time Per Level</div>
        </div>
        <div style={{
          width: '100%',
          height: 'min-content',
          overflowX: 'auto',
          overflowY: 'hidden',
          position: 'relative'
        }}>
          <div style={{
            height: '100%',
            minWidth: '100%',
            width: pathWidth // Makes each bar take up ~12.5% of container width
          }}>
            <ResponsiveContainer
              width="100%"
              height={window.innerWidth <= 768 ? 300 : 400}
            >
              <BarChart
                data={chartData}
                data-testid="barchart-cannulation-times"
                margin={{
                  top: window.innerWidth <= BREAKPOINT_MOBILE ? 16 : 24,
                  right: window.innerWidth <= BREAKPOINT_MOBILE ? 16 : 24,
                  left: window.innerWidth <= BREAKPOINT_MOBILE ? 16 : 24,
                  bottom: window.innerWidth <= BREAKPOINT_MOBILE ? 16 : 24,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="vertebra"
                  height={window.innerWidth <= BREAKPOINT_MOBILE ? 20 : 80}
                  tick={window.innerWidth <= BREAKPOINT_MOBILE ? (props): React.JSX.Element => (
                    <g transform={`translate(${props.x},${props.y})`}>
                      <text
                        x={0}
                        y={0}
                        dy={2}
                        textAnchor="middle"
                        fill="#666666"
                        fontSize={12}
                      >
                        {props.payload.value}
                      </text>
                    </g>
                  ) : undefined}
                  interval={0}
                  tickMargin={window.innerWidth <= BREAKPOINT_MOBILE ? 20 : 12}
                />
                <YAxis
                  label={{
                    value: 'Minutes',
                    angle: -90,
                    position: 'insideLeft',
                    style: {
                      textAnchor: 'middle',
                      fontSize: window.innerWidth <= BREAKPOINT_MOBILE ? 12 : 14
                    }
                  }}
                  tickFormatter={value => value.toFixed(1)}
                  fontSize={window.innerWidth <= BREAKPOINT_MOBILE ? 12 : 14}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="time"
                  name="Cannulation Time"
                  fill="#8E3DBC"
                  radius={[4, 4, 0, 0]}
                  label={{
                    position: 'top',
                    content: props => (
                      <text
                        x={Number(props.x) + Number(props.width) / 2}
                        y={Number(props.y) - 10}
                        fill="#333"
                        textAnchor="middle"
                        fontSize={window.innerWidth <= BREAKPOINT_MOBILE ? 10 : 12}
                      >
                        {Number(props.value).toFixed(1)}
                      </text>
                    )
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  // Render the path lengths chart
  const renderPathLengthsChart = (): React.JSX.Element => {
    if (efficiencyData.pathLengths.length === 0) {
      return (
        <div className="warning-message">
          <p>No path length data available for this case.</p>
        </div>
      );
    }

    // Group and format data for the stacked bar chart
    const vertebraSet = new Set(efficiencyData.pathLengths.map(item => item.vertebra));
    const toolNames = [...new Set(efficiencyData.pathLengths.map(item => item.tool_name))];

    // Create formatted data for the chart
    const chartData: VertebraPathLength[] = [];
    vertebraSet.forEach(vertebra => {
      const vertebraItems = efficiencyData.pathLengths.filter(item => item.vertebra === vertebra);

      // Create a data point for this vertebra
      const dataPoint: any = {
        vertebra: vertebra,
        // Calculate total for the label
        total: vertebraItems.reduce((sum, item) => sum + item.path_length_meters, 0)
      };

      // Add each tool's path length to the data point
      toolNames.forEach(tool => {
        const toolItem = vertebraItems.find(item => item.tool_name === tool);
        dataPoint[tool] = toolItem ? toolItem.path_length_meters : 0;
      });

      chartData.push(dataPoint);
    });

    // Sort by vertebra
    chartData.sort((a: VertebraPathLength, b: VertebraPathLength): number => {
      return adapter.getVertebraSortOrder(a.vertebra) - adapter.getVertebraSortOrder(b.vertebra);
    });

    // Colors for tools
    const toolColors = new Map([
      ['Blunt Probe', '#4C9FFF'],   // Blue
      ['Straight Probe', '#00C19B']  // Green
    ]);

    return (
      <div>
        <div className="total-time-container">
          <span className="total-time-value">{getAvgPathLengthText()}</span>
          <div className="total-time-label">Avg. Path Length</div>
        </div>
        <div style={{ width: '100%', height: 400 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data-testid="barchart-pathlengths"
              data={chartData}
              margin={{
                top: window.innerWidth <= BREAKPOINT_MOBILE ? 0 : 24,
                right: window.innerWidth <= BREAKPOINT_MOBILE ? 16 : 24,
                left: window.innerWidth <= BREAKPOINT_MOBILE ? 16 : 24,
                bottom: window.innerWidth <= BREAKPOINT_MOBILE ? 16 : 16,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              {window.innerWidth <= BREAKPOINT_MOBILE ? (
                <Fragment>
                  <XAxis
                    dataKey="vertebra"
                    height={20}
                    tick={props => (
                      <g transform={`translate(${props.x},${props.y})`}>
                        <text
                          x={0}
                          y={0}
                          dy={2}
                          textAnchor="middle"
                          fill="#666666"
                          fontSize={12}
                        >
                          {props.payload.value}
                        </text>
                      </g>
                    )}
                    interval={0}
                    tickMargin={20}
                  />
                </Fragment>
              ) : (
                <XAxis
                  dataKey="vertebra"
                  height={80}
                  tickMargin={12}
                />
              )}
              <YAxis
                label={{
                  value: 'Path Length (meters)',
                  angle: -90,
                  position: 'insideLeft',
                  style: {
                    textAnchor: 'middle',
                    fontSize: window.innerWidth <= BREAKPOINT_MOBILE ? 12 : 14
                  }
                }}
                tickFormatter={value => value.toFixed(1)}
                fontSize={window.innerWidth <= BREAKPOINT_MOBILE ? 12 : 14}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="top"
                align="center"
                wrapperStyle={{

                  fontSize: window.innerWidth <= BREAKPOINT_MOBILE ? '10px' : '12px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  gap: '16px',
                  position: 'relative',
                  bottom: 15,
                  top: 15,
                  left: 0,
                  right: 0,
                }}
                formatter={value => (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '4px 8px',
                    fontSize: window.innerWidth <= BREAKPOINT_MOBILE ? '10px' : '12px',
                    lineHeight: '1',
                    color: '#666666'
                  }}>
                    {value}
                  </span>
                )}
                iconSize={10}
                payload={toolNames.map(tool => ({
                  value: tool,
                  type: 'square',
                  color: toolColors.get(tool)
                }))}
              />
              {toolNames.map(tool => (
                <Bar
                  key={tool}
                  dataKey={tool}
                  name={tool}
                  stackId="a"
                  fill={toolColors.get(tool)}
                  radius={toolNames.indexOf(tool) === toolNames.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                />
              ))}
              <Bar
                dataKey="total"
                name=""
                fill="none"
                label={{
                  position: 'top',
                  content: props => (
                    <text
                      x={Number(props.x) + Number(props.width) / 2}
                      y={Number(props.y) - 10}
                      fill="#333"
                      textAnchor="middle"
                      fontSize={window.innerWidth <= BREAKPOINT_MOBILE ? 10 : 12}
                    >
                      {Number(props.value).toFixed(1)}
                    </text>
                  )
                }}
                hide={true}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const getAvgCannulationTimeText = (): string => {
    if (!efficiencyData.cannulationTimes || efficiencyData.cannulationTimes.length === 0) {
      return 'NA';
    }
    const total = efficiencyData.cannulationTimes.reduce((sum, item) => sum + item.time_minutes, 0);
    return (total / efficiencyData.cannulationTimes.length).toFixed(1) + ' min';
  };

  const getAvgPathLengthText = (): string => {
    if (!efficiencyData.pathLengths || efficiencyData.pathLengths.length === 0) {
      return 'NA';
    }
    const total = efficiencyData.pathLengths.reduce((sum, item) => sum + item.path_length_meters, 0);
    return (total / efficiencyData.pathLengths.length).toFixed(1) + ' m';
  };

  if (loading) {
    return <div className="loading">Loading cannulation technique data...</div>;
  }

  if (error && efficiencyData.cannulationTimes.length === 0 && efficiencyData.pathLengths.length === 0) {
    return (
      <div className="cannulation-technique-container">
        <div className="section-title">Cannulation & Screw Placement Efficiency</div>
        <div className="error-message">
          <p>Cannulation technique data is not available for this case.</p>
          <p className="error-details">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cannulation-technique-container">
      <div className="case-graph-container">
        <div className="chart-container">
          <div className="section-title">Cannulation & Screw Placement Efficiency</div>
          {renderCannulationTimesChart()}
        </div>
      </div>

      {
        <div className="case-graph-container">
          <div className="chart-container">
            <div className="section-title">Instrument Path Lengths</div>
            {
              renderPathLengthsChart()
            }
          </div>
        </div>
      }
    </div>
  );
};
export default CannulationTechnique;
